import type { JSX } from "react";
import { useLayoutEffect, useState } from "react";

import type { IconName } from "@similarweb/icons";
import type { ButtonType } from "@similarweb/ui-components";
import { Button, IconButton, PlainTooltip } from "@similarweb/ui-components";

import type { IShareUserExtended } from "app/sharing/SharingService";
import { TooltipContent, ButtonWrapper } from "pro-features/components/Buttons/style/ShareDashboardButton.styled";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";

interface ISharedDashboardButtonProps {
    isDisabled?: boolean;
    onClick: () => void;
    isDataLoading: boolean;
    sharedWithAccounts: number[];
    dashboardViewers: IShareUserExtended[];
    isTooltipVisible?: boolean;
    buttonType?: ButtonType;
    buttonWidth?: string;
    initialButtonText?: string;
    initialIconName?: IconName;
}

const formatUserName = (users: IShareUserExtended[]) => {
    if (!users[0].firstName && !users[0].lastName) {
        return users[0].email;
    }

    return `${users[0]?.firstName} ${users[0]?.lastName}`;
};

export const ShareDashboardButton = ({
    sharedWithAccounts,
    dashboardViewers,
    onClick,
    isDataLoading,
    isDisabled,
    isTooltipVisible = true,
    buttonType = "flat",
    buttonWidth = "100px",
    initialButtonText = "dashboard.shareDashboard.button.text.share",
    initialIconName = "employees",
}: ISharedDashboardButtonProps): JSX.Element => {
    const t = useTranslation();
    const [iconName, setIconName] = useState<IconName>(initialIconName);
    const [tooltipText, setTooltipText] = useState<string>("dashboard.shareDashboard.tooltip.text.noshare");
    const [tooltipParams, setTooltipParams] = useState<{ user?: string; count?: string }>({});
    const [buttonText, setButtonText] = useState<string>(initialButtonText);

    useLayoutEffect(() => {
        if (!isDataLoading) {
            if (sharedWithAccounts.length > 0) {
                setIconName("company");
                setTooltipText("dashboard.shareDashboard.tooltip.text");
                setButtonText("dashboard.shareDashboard.button.text.shared");
            } else {
                switch (dashboardViewers.length) {
                    case 0:
                        break;
                    case 1:
                        setIconName("users");
                        setTooltipText("dashboard.shareDashboard.tooltip.text.oneuser");
                        setTooltipParams({
                            user: formatUserName(dashboardViewers),
                        });
                        setButtonText("dashboard.shareDashboard.button.text.shared");
                        break;
                    default:
                        setIconName("users");
                        setTooltipText("dashboard.shareDashboard.tooltip.text.multipleusersUpdated");
                        setTooltipParams({
                            user: formatUserName(dashboardViewers),
                            count: (dashboardViewers.length - 1).toString(),
                        });
                        setButtonText("dashboard.shareDashboard.button.text.shared");
                        break;
                }
            }
        }
    }, [sharedWithAccounts, dashboardViewers, isDisabled, isDataLoading]);

    const button = (
        <ButtonWrapper buttonWidth={buttonWidth}>
            {initialIconName ? (
                <IconButton
                    isDisabled={isDisabled}
                    isLoading={isDataLoading}
                    type={buttonType}
                    iconName={iconName}
                    onClick={onClick}
                >
                    {t(buttonText)}
                </IconButton>
            ) : (
                <Button
                    isDisabled={isDisabled}
                    isLoading={isDataLoading}
                    type={buttonType}
                    onClick={onClick}
                >
                    {t(buttonText)}
                </Button>
            )}
        </ButtonWrapper>
    );

    //fast return if disabled or tooltip not visible
    if (isDisabled || !isTooltipVisible) {
        return button;
    }

    const tooltipContent: JSX.Element = <TooltipContent>{t(tooltipText, tooltipParams)}</TooltipContent>;

    return (
        <PlainTooltip
            tooltipContent={tooltipContent}
            placement="bottom"
        >
            {button}
        </PlainTooltip>
    );
};
